import {
  PusherSynonymsFeedback,
  SetError,
  WtPusherEvent,
  SetIsLoadingSuggestions,
} from '@chegg-wt/core-wa/state/types'
import {
  SYNONYM_REWRITE_FETCH_RETRY_COUNT,
  SELECTION_CHECK_TIMEOUT,
} from '@chegg-wt/core-wa/constants/paraphraser'
import { oneGraph } from '@chegg-wt/core/api/oneGraph'
import { CheckTypeInput } from '@chegg-wt/core/globalTypesOneGraph'

class SynonymsDataHandler {
  private sentenceId: string

  private originalText: string

  private timeout: ReturnType<typeof setTimeout>

  private retriesLeft: number

  private traceId: string

  private paperId: string

  private initiatedAt: string

  private synonymPosition: { offset: number; length: number } = { offset: 0, length: 0 }

  public disableSynonymFeedbackAcceptance = () => {
    this.sentenceId = null
    this.initiatedAt = null
    this.clearExistingTimeout()
  }

  public enableSynonymFeedbackAcceptance = (
    sentenceId: string,
    originalText: string,
    initiatedAt: string,
    traceId: string,
    paperId: string,
    length: number,
    offset: number
  ) => {
    this.sentenceId = sentenceId
    this.originalText = originalText
    this.initiatedAt = initiatedAt
    this.traceId = traceId
    this.paperId = paperId
    this.synonymPosition.offset = offset
    this.synonymPosition.length = length
    this.retriesLeft = SYNONYM_REWRITE_FETCH_RETRY_COUNT
  }

  private clearExistingTimeout = () => {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  public handleSynonymCall = (
    traceId: string,
    paperId: string,
    sentences: { id: string; originalText: string }[],
    completeWordsLength: number,
    oldStart: number,
    initiatedAt: string,
    setIsLoadingSuggestions: SetIsLoadingSuggestions,
    setError: SetError
  ) => {
    // Clear old timeout for this synonym
    this.clearExistingTimeout()
    setError(null)

    // Add a new timeout
    this.timeout = setTimeout(() => {
      this.disableSynonymFeedbackAcceptance()
      setIsLoadingSuggestions(false)
      setError({ code: 'TIMEOUT', message: 'Timeout in fetching synonyms' })
    }, SELECTION_CHECK_TIMEOUT)

    return oneGraph
      .sentenceCheck({
        request: {
          checkType: CheckTypeInput.SYNONYM_SELECTION,
          traceId,
          paperId,
          sentences,
          synonymPosition: {
            length: completeWordsLength,
            offset: oldStart,
          },
          initiatedAt,
        },
      })
      .catch((error: Error) => {
        setIsLoadingSuggestions(false)
        setError({ code: 'SERVER_ERROR', message: error.message })
        this.disableSynonymFeedbackAcceptance()
      })
  }

  private handleRetry(
    sentences: { id: string; originalText: string }[],
    initiatedAt: string,
    setIsLoadingSuggestions: SetIsLoadingSuggestions,
    setError: SetError
  ) {
    const { offset, length } = this.synonymPosition

    this.handleSynonymCall(
      this.traceId,
      this.paperId,
      sentences,
      length,
      offset,
      initiatedAt,
      setIsLoadingSuggestions,
      setError
    )
    this.retriesLeft -= 1
  }

  public handleSynonymFeedbackEvent = (
    eventData: WtPusherEvent,
    handleSynonymData: (synonyms: string[]) => void,
    setIsLoadingSuggestions: SetIsLoadingSuggestions,
    setError: SetError
  ): void => {
    // we will only consider synonym events with same sentence id and same initiated time
    if (
      eventData.sentence.id === this.sentenceId &&
      eventData.initiatedAt === this.initiatedAt
    ) {
      if (eventData.error) {
        if (this.retriesLeft > 0) {
          this.handleRetry(
            [
              {
                id: this.sentenceId,
                originalText: this.originalText,
              },
            ],
            eventData.initiatedAt,
            setIsLoadingSuggestions,
            setError
          )
          return
        }
        setError({ code: 'API_ERROR', message: eventData.error.message })
        setIsLoadingSuggestions(false)
        this.disableSynonymFeedbackAcceptance()
        return
      }

      const feedback = eventData.feedback as PusherSynonymsFeedback
      if (feedback) {
        this.disableSynonymFeedbackAcceptance()
        handleSynonymData(feedback.synonyms)
      }
    }
  }
}

const synonymsDataHandler = new SynonymsDataHandler()
export default synonymsDataHandler
